import React, { useState, useEffect, useRef } from 'react';
import { ChromePicker, ColorResult } from 'react-color';
import { 
  IconButton, 
  Typography,
  Box,
  CircularProgress,
  Popover,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { projectService } from '../../services/project';
import { getBoardTemplates } from '../../services/boardTemplate';
import { BoardTemplate } from '../../interfaces/boardTemplate';
import BoardTemplateManager from '../boardTemplateManager';
import { 
  BoardDialog,
  BoardDialogTitle,
  BoardDialogContent,
  BoardDialogActions,
  BoardPreviewSection,
  BackgroundOptionsList,
  BackgroundOption,
  MoreOptionsButton,
  SectionLabel,
  RequiredMark,
  BoardTitleField,
  WarningText,
  CreateButton,
  DividerWrapper,
  CustomDivider,
} from './styles';

interface CreateBoardModalProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onBoardCreated?: (boardId: string) => void;
  onBoardUpdated?: () => void;
  title?: string;
  placeholder?: string;
  editMode?: boolean;
  boardData?: {
    id: string;
    title: string;
    backgroundColor: string;
  };
}

const backgroundOptions = [
  { id: 'bg1', color: '#0D2249' },
  { id: 'bg2', color: '#3498DB' },
  { id: 'bg3', color: '#216ED3' },
  { id: 'bg4', color: '#8E44AD' },
  { id: 'bg5', color: '#D881ED' },
  { id: 'bg6', color: '#27AE60' }, // Verde
  { id: 'bg7', color: '#E67E22' }, // Laranja
  { id: 'bg8', color: '#E74C3C' }, // Vermelho
  { id: 'bg9', color: '#34495E' }, // Azul escuro
  { id: 'custom', color: '#FFFFFF' }, // Cor personalizada
];

export default function CreateBoardModal({ 
  open, 
  projectId, 
  onClose, 
  onBoardCreated, 
  onBoardUpdated,
  title = 'Criar Quadro', 
  placeholder = 'Adicione um título para o quadro',
  editMode = false,
  boardData
}: CreateBoardModalProps) {
  const [boardTitle, setBoardTitle] = useState('');
  const [selectedBackground, setSelectedBackground] = useState(backgroundOptions[0].id);
  const [isTitleValid, setIsTitleValid] = useState(false);
  const [touched, setTouched] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para o seletor de cores personalizado
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState('#FFFFFF');
  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState<HTMLElement | null>(null);

  // Estados para templates
  const [boardTemplates, setBoardTemplates] = useState<BoardTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isTemplateManagerOpen, setIsTemplateManagerOpen] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (open) {
      if (editMode && boardData) {
        // Se estiver em modo de edição, preencha os campos com os dados existentes
        setBoardTitle(boardData.title);
        
        // Encontre o ID do background que corresponde à cor
        const bgOption = backgroundOptions.find(bg => bg.color === boardData.backgroundColor);
        if (bgOption) {
          setSelectedBackground(bgOption.id);
        } else {
          // Se a cor não está nas opções padrão, define como cor personalizada
          setSelectedBackground('custom');
          setCustomColor(boardData.backgroundColor);
          // Atualiza a cor da opção personalizada
          backgroundOptions[backgroundOptions.length - 1].color = boardData.backgroundColor;
        }
        
        // Considere o título já válido
        setIsTitleValid(boardData.title.trim().length > 0);
      } else {
        // Reset para o modo de criação
        setBoardTitle('');
        setSelectedBackground(backgroundOptions[0].id);
        setIsTitleValid(false);
        setCustomColor('#FFFFFF');
        setSelectedTemplateId('');
      }
      setTouched(false);
      setError(null);
      setShowColorPicker(false);
      setColorPickerAnchorEl(null);
      
      // Carregar templates de board
      loadBoardTemplates();
    }
  }, [open, editMode, boardData]);
  
  // Carregar templates de board
  const loadBoardTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const templates = await getBoardTemplates();
      setBoardTemplates(templates);
    } catch (error) {
      console.error('Erro ao carregar templates de board:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setBoardTitle(newTitle);
    setIsTitleValid(newTitle.trim().length > 0);
    if (!touched) setTouched(true);
  };

  const handleBackgroundSelect = (bgId: string) => {
    if (bgId !== 'custom') {
      setSelectedBackground(bgId);
    } else {
      // Se clicar na cor personalizada (o último botão), abrir o seletor de cores
      handleOpenColorPicker(null);
    }
  };
  
  const handleOpenColorPicker = (event: React.MouseEvent<HTMLElement> | null) => {
    if (event) {
      setColorPickerAnchorEl(event.currentTarget);
    }
    setShowColorPicker(true);
    setSelectedBackground('custom');
  };
  
  const handleCloseColorPicker = () => {
    setShowColorPicker(false);
    setColorPickerAnchorEl(null);
  };
  
  const handleColorChange = (color: ColorResult) => {
    setCustomColor(color.hex);
    // Atualiza a cor da opção personalizada
    backgroundOptions[backgroundOptions.length - 1].color = color.hex;
    // Seleciona a opção personalizada
    setSelectedBackground('custom');
  };

  const handleSubmitBoard = async () => {
    if (!isTitleValid || isCreating) return;

    setIsCreating(true);
    setError(null);

    // Obter a cor de fundo selecionada
    let boardColor = '';
    if (selectedBackground === 'custom') {
      boardColor = customColor;
    } else {
      const selectedBg = backgroundOptions.find(bg => bg.id === selectedBackground);
      boardColor = selectedBg ? selectedBg.color : customColor;
    }
    
    const boardFormData = {
      title: boardTitle.trim(),
      backgroundColor: boardColor,
      projectId,
      templateId: selectedTemplateId || undefined
    };

    try {
      if (editMode && boardData) {
        // Modo edição - atualizar board existente
        
        // Atualizar o board no backend
        await projectService.updateBoard(boardData.id, boardFormData);

        // Notificar o componente pai sobre a atualização
        if (onBoardUpdated) onBoardUpdated();
      } else {
        // Modo criação - criar novo board
        
        // Criar o quadro no backend
        const newBoard = await projectService.createBoard(projectId, boardFormData);

        // Notificar o componente pai sobre o novo quadro
        if (onBoardCreated) onBoardCreated(newBoard.id);
      }
      
      // Fechar o modal
      onClose();
    } catch (err: any) {
      console.error(`Erro ao ${editMode ? 'atualizar' : 'criar'} quadro:`, err);
      console.error('Detalhes do erro:', err.response?.data || err.message);
      setError(`Ocorreu um erro ao ${editMode ? 'atualizar' : 'criar'} o quadro: ${err.message}. Por favor, tente novamente.`);
    } finally {
      setIsCreating(false);
    }
  };

  // Obtém a cor selecionada, seja uma opção padrão ou personalizada
  const getSelectedColor = () => {
    if (selectedBackground === 'custom') {
      return customColor;
    }
    const selectedBg = backgroundOptions.find(bg => bg.id === selectedBackground);
    return selectedBg ? selectedBg.color : customColor;
  };
  
  const handleTemplateChange = (event: SelectChangeEvent<string>) => {
    setSelectedTemplateId(event.target.value);
  };

  const handleTemplateManagerOpen = () => {
    setIsTemplateManagerOpen(true);
  };

  const handleTemplateSelected = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  // Detecta cores claras ou escuras para ajustar elementos de UI
  const isLightColor = (color: string) => {
    try {
      // Remove o # se existir
      const hex = color.replace('#', '');
      // Converte para RGB
      const r = parseInt(hex.substring(0, 2), 16) || 0;
      const g = parseInt(hex.substring(2, 4), 16) || 0;
      const b = parseInt(hex.substring(4, 6), 16) || 0;
      // Calcula luminosidade aproximada
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
    } catch (error) {
      return false; // Assume cor escura em caso de erro
    }
  };

  return (
    <BoardDialog
      open={open}
      onClose={onClose}
      aria-labelledby="create-board-dialog-title"
    >
      <BoardDialogTitle id="create-board-dialog-title">
        {editMode ? 'Editar Quadro' : title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: 8, 
            top: 8, 
            color: 'white' 
          }}
          size="small"
          disabled={isCreating}
        >
          <CloseIcon />
        </IconButton>
      </BoardDialogTitle>

      <DividerWrapper>
        <CustomDivider />
      </DividerWrapper>
      
      <BoardDialogContent>
        <BoardPreviewSection>
          <Box 
            sx={{ 
              height: 120, 
              backgroundColor: getSelectedColor(),
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 1
            }}
          >
          </Box>
        </BoardPreviewSection>

        <SectionLabel variant="subtitle2">Tela de Fundo</SectionLabel>
        <BackgroundOptionsList sx={{ padding: '0 4px' }}>
          {/* Primeira fila de cores (5 cores) */}
          {backgroundOptions.slice(0, 5).map((bg) => (
            <BackgroundOption
              key={bg.id}
              selected={selectedBackground === bg.id}
              onClick={() => handleBackgroundSelect(bg.id)}
              sx={{ backgroundColor: bg.color }}
            />
          ))}
        </BackgroundOptionsList>
        
        <BackgroundOptionsList sx={{ padding: '0 4px', mt: 1 }}>
          {/* Segunda fila de cores (4 cores + o picker) */}
          {backgroundOptions.slice(5, 9).map((bg) => (
            <BackgroundOption
              key={bg.id}
              selected={selectedBackground === bg.id}
              onClick={() => handleBackgroundSelect(bg.id)}
              sx={{ backgroundColor: bg.color }}
            />
          ))}
          
          {/* Botão do seletor de cores personalizado que mostra a cor atual */}
          <MoreOptionsButton 
            onClick={(e) => handleOpenColorPicker(e)}
            sx={{ 
              backgroundColor: customColor,
              border: selectedBackground === 'custom' ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <ColorLensIcon sx={{ 
              color: isLightColor(customColor) ? 'rgba(0, 0, 0, 0.7)' : 'white', 
              fontSize: 20, 
              filter: selectedBackground === 'custom' ? 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.7))' : 'none' 
            }} />
          </MoreOptionsButton>
        </BackgroundOptionsList>

        <Popover
          open={showColorPicker}
          anchorEl={colorPickerAnchorEl}
          onClose={handleCloseColorPicker}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ p: 1, backgroundColor: '#2d3748' }}>
            <ChromePicker 
              color={customColor} 
              onChange={handleColorChange} 
              disableAlpha
            />
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                size="small"
                onClick={handleCloseColorPicker}
                sx={{ 
                  bgcolor: '#4299e1', 
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#3182ce',
                  }
                }}
              >
                Aplicar
              </Button>
            </Box>
          </Box>
        </Popover>

        <SectionLabel variant="subtitle2">
          Título do quadro
          <RequiredMark>*</RequiredMark>
        </SectionLabel>
        <BoardTitleField
          autoFocus
          fullWidth
          variant="outlined"
          value={boardTitle}
          onChange={handleTitleChange}
          isError={touched && !isTitleValid}
          placeholder={placeholder}
          disabled={isCreating}
        />
        {touched && !isTitleValid && (
          <WarningText>
            <WarningAmberIcon />
            <Typography variant="caption">O título do quadro é obrigatório</Typography>
          </WarningText>
        )}
        
        {!editMode && (
          <>
            <SectionLabel variant="subtitle2" sx={{ mt: 2 }}>
              Template (opcional)
            </SectionLabel>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl fullWidth>
                <Select
                  value={selectedTemplateId}
                  onChange={handleTemplateChange}
                  displayEmpty
                  size="small"
                  disabled={isCreating}
                >
                  <MenuItem value="">
                    <em>Sem template</em>
                  </MenuItem>
                  {boardTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                variant="outlined" 
                onClick={handleTemplateManagerOpen}
                disabled={isCreating}
              >
                Gerenciar
              </Button>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Selecionar um template criará automaticamente as colunas predefinidas para o board.
              </Typography>
            </Box>
          </>
        )}
        
        {error && (
          <WarningText>
            <WarningAmberIcon />
            <Typography variant="caption">{error}</Typography>
          </WarningText>
        )}
      </BoardDialogContent>

      <BoardDialogActions>
        <CreateButton 
          fullWidth 
          onClick={handleSubmitBoard}
          isValid={isTitleValid && !isCreating}
          disabled={isCreating}
        >
          {isCreating ? <CircularProgress size={24} color="inherit" /> : (editMode ? "Salvar" : "Criar")}
        </CreateButton>
      </BoardDialogActions>

      {/* Gerenciador de Templates */}
      <BoardTemplateManager 
        open={isTemplateManagerOpen}
        onClose={() => setIsTemplateManagerOpen(false)}
        onTemplateSelected={handleTemplateSelected}
        selectMode={true}
      />
    </BoardDialog>
  );
}